/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-11 21:25:55
 **/

import { PropsWithChildren, ReactNode, useLayoutEffect, useState } from "react";
import { Properties } from "@/properties";
import { useBeforeUnload } from "@/hooks";

/**
 * Represents the properties required for the rehydration process.
 *
 * @interface RehydrationProps
 * @property {boolean} [auto_selecting] Optional property indicating whether auto-selecting is enabled.
 * @property {boolean} is_hydrated Required property indicating whether the rehydration process is complete.
 * @property {() => Promise<number>} on_initialized Required callback method triggered when initialization has started. Returns a promise resolving to a number.
 * @property {() => void} [on_finalized] Optional callback method triggered when the process is finalized.
 * @property {(result: number) => ReactNode} [on_initialized_failure] Optional callback method triggered when initialization fails. Accepts a number representing the failure result and returns a ReactNode.
 * @property {ReactNode} [on_loading] Optional property representing the UI or component to render during the loading state.
 */
interface RehydrationProps {
	auto_selecting?: boolean;
	is_hydrated: boolean;
	on_initialized: () => Promise<number>;
	on_finalized?: () => void;
	on_initialized_failure?: (result: number) => ReactNode;
	on_loading?: ReactNode;
}

/**
 * Handles the rehydration process in a React Component by initializing, finalizing actions, and determining the state of readiness.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} props.is_hydrated - Indicates whether the rehydration process is ready to be initialized.
 * @param {Function} [props.on_initialized] - An optional asynchronous callback triggered upon initialization.
 * @param {Function} [props.on_finalized] - An optional callback triggered upon finalization.
 * @param {Function} [props.on_initialized_failure] - An optional fallback node or component rendered when initialization fails.
 * @param {boolean} [props.auto_selecting=true] - Determines if auto-select functionality is enabled.
 * @param {ReactNode} [props.children] - Content or components to be displayed when the component is rehydrated successfully.
 * @param {ReactNode} [props.fallback] - A fallback node or value displayed while the component is not ready.
 * @return {ReactNode} A node or component rendered based on the rehydration process and readiness state.
 */
export default function Rehydration(props: PropsWithChildren<RehydrationProps>): ReactNode {
	const { is_hydrated, on_initialized, on_finalized, on_initialized_failure, auto_selecting = true } = props;
	const [result, set_result] = useState<number | undefined>(undefined);

	useLayoutEffect(() => {
		Properties.instance.use_auto_select = auto_selecting;
		if (is_hydrated) {
			if (auto_selecting && Properties.instance.is_global_selected) {
				Properties.instance.selected = Properties.instance.get_global("selected");
			}
			Properties.instance.update_browser_tab_id();
			if (on_initialized) {
				on_initialized().then((state: number) => set_result(state));
			} else {
				set_result(0);
			}
		}
	}, [auto_selecting, is_hydrated, on_initialized]);

	useBeforeUnload(() => {
		Properties.instance.dispose_browser_tab_id();
		on_finalized?.();
	});

	return props.is_hydrated && result !== undefined
		? result === 0
			? props.children
			: (on_initialized_failure?.(result) ?? props.children)
		: (props.on_loading ?? null);
}
